import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getUpdatedSelectedOptionValues, UpdateExisting } from './helpers/sliceHelpers';
// Do not import from thunk index file as submitPersonalisation form imports from this file and makes a circular dependency
import { updatePersonalisationOptionsFromBasketItem } from './thunks/updatePersonalisationOptionsFromBasketItem';
import {
  InteractionErrorTrackingEvent,
  InteractionTrackingEvent,
  PersonalisationState,
  ProgressTrackingEvent,
  SelectedPersonalisationsRecord,
  SelectionPayload,
} from './types';

export const initialState: Readonly<PersonalisationState> = {
  editedItem: null,
  error: null,
  selectedPersonalisations: {} as SelectedPersonalisationsRecord,
  isPersonalisationOptionsExpanded: false,
  shouldShowPersonalisationValidationErrors: false,
  trackedEvents: {
    focus: {},
    progress: {
      'Progress - 50%': false,
      'Progress - 100%': false,
    },
    error: {
      'Character Limit Error': {},
      'Add to Cart Error': {},
    },
  },
};

export const { actions, reducer } = createSlice({
  name: 'personalisation',
  initialState,
  reducers: {
    showPersonalisationValidationErrors: (state) => {
      state.shouldShowPersonalisationValidationErrors = true;
    },
    deselectPersonalisation: (state, { payload }: PayloadAction<SelectionPayload>) => {
      delete state.selectedPersonalisations[payload.optionId];
    },
    selectPersonalisation: (state, { payload }: PayloadAction<SelectionPayload>) => {
      state.selectedPersonalisations = getUpdatedSelectedOptionValues(
        state.selectedPersonalisations,
        payload,
        UpdateExisting.ChangeInPlace,
      );
    },
    toggleSelectPersonalisation: (state, { payload }: PayloadAction<SelectionPayload>) => {
      state.selectedPersonalisations = getUpdatedSelectedOptionValues(
        state.selectedPersonalisations,
        payload,
        UpdateExisting.Dedupe,
      );
    },
    toggleExpanded: (state) => {
      state.isPersonalisationOptionsExpanded = true;
    },
    trackInteraction: (state, { payload }: PayloadAction<InteractionTrackingEvent>) => {
      if (payload.field_action === 'Field Focus') {
        state.trackedEvents.focus[payload.field_name] = true;
      } else {
        state.trackedEvents.progress[payload.field_name] = true;
      }
    },
    trackProgressMilestone: (state, { payload }: PayloadAction<ProgressTrackingEvent>) => {
      state.trackedEvents.progress[payload.field_action] = true;
      state.trackedEvents.progress[payload.field_name] = true;
    },
    trackInteractionError: (state, { payload }: PayloadAction<InteractionErrorTrackingEvent>) => {
      state.trackedEvents.error[payload.field_action][payload.field_name] = true;
    },
    updateEditedItem: (state, { payload }: PayloadAction<string>) => {
      state.editedItem = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updatePersonalisationOptionsFromBasketItem.fulfilled, (state, { payload }) => {
      if (!payload) return;

      for (const option of payload.options) {
        if (!state.selectedPersonalisations[option.optionId]) {
          state.selectedPersonalisations[option.optionId] = [];
        }

        state.selectedPersonalisations[option.optionId]?.push({
          id: parseInt(option.valueId || option.optionId),
          name: option.value || option.valueLabel!,
          priceModifier: null,
          isCustomInput: !!option.value,
        });
      }
    });
  },
});
